import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { acquireNamToken, fetchNamSuggestions, getNamConfig } from '../../../api/nam_api';
import '../../../asset/css/nam-search.css';

export default function NamAddressSearch({ onSelect, onClear }) {
    const { instance, accounts } = useMsal();
    const account = accounts[0];
    const configured = getNamConfig().configured;
    const inputRef = useRef(null);
    const caretRef = useRef(null);
    const requestRef = useRef(0);
    const controllerRef = useRef(null);
    const mountedRef = useRef(true);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(false);
    const [retry, setRetry] = useState(0);
    const [authorizing, setAuthorizing] = useState(false);
    const [interactionRequired, setInteractionRequired] = useState(false);

    const reportError = error => {
        setInteractionRequired(error instanceof InteractionRequiredAuthError);
        setError(error instanceof InteractionRequiredAuthError
            ? 'Your session requires additional sign-in or permission for address search. Select Sign in to continue.'
            : `NAM address search failed: ${error.message}`);
    };

    useEffect(() => {
        if (Number.isInteger(caretRef.current)) {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(caretRef.current, caretRef.current);
            caretRef.current = null;
        }
    }, [query, retry]);

    useEffect(() => {
        const requestId = ++requestRef.current;
        const controller = new AbortController();
        controllerRef.current = controller;
        setSuggestions([]);
        setOpen(false);
        setHighlighted(-1);
        if (!selected) setBusy(false);
        if (!configured || selected || query.trim().length < 4) {
            return () => controller.abort();
        }
        const timer = setTimeout(async () => {
            if (controller.signal.aborted) return;
            setBusy(true);
            setError('');
            try {
                const results = await fetchNamSuggestions(instance, account, query.trim(), controller.signal);
                if (requestId !== requestRef.current || controller.signal.aborted) return;
                setInteractionRequired(false);
                setSuggestions(results);
                setOpen(results.length > 0);
                if (!results.length) setError('No matching NAM addresses found.');
            } catch (error) {
                if (requestId === requestRef.current && !controller.signal.aborted) reportError(error);
            } finally {
                if (requestId === requestRef.current && !controller.signal.aborted) setBusy(false);
            }
        }, 300);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [query, selected, retry, configured, instance, account]);

    useEffect(() => () => {
        mountedRef.current = false;
        requestRef.current += 1;
        controllerRef.current?.abort();
    }, []);

    const changeQuery = value => {
        requestRef.current += 1;
        controllerRef.current?.abort();
        setQuery(value);
        caretRef.current = null;
        setSelected(false);
        setError('');
        onClear();
    };

    const selectSuggestion = suggestion => {
        controllerRef.current?.abort();
        requestRef.current += 1;
        setSuggestions([]);
        setOpen(false);
        setHighlighted(-1);
        setQuery(suggestion.text);
        setError('');
        if (!suggestion.data?.id) {
            caretRef.current = suggestion.caretPosition;
            setSelected(false);
            setRetry(value => value + 1);
            return;
        }
        setSelected(true);
        setBusy(false);
        onSelect(suggestion.data);
    };

    const authorize = async () => {
        setError('');
        setAuthorizing(true);
        try {
            await acquireNamToken(instance, account, true);
            if (!mountedRef.current) return;
            setInteractionRequired(false);
            setSelected(false);
            setRetry(value => value + 1);
        } catch (error) {
            if (mountedRef.current) {
                reportError(error);
                setInteractionRequired(true);
            }
        } finally {
            if (mountedRef.current) setAuthorizing(false);
        }
    };

    const handleKeyDown = event => {
        if (event.key === 'Escape') {
            requestRef.current += 1;
            controllerRef.current?.abort();
            setBusy(false);
            setOpen(false);
        } else if (open && suggestions.length) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlighted(index => event.key === 'ArrowDown'
                    ? (index + 1) % suggestions.length
                    : (index <= 0 ? suggestions.length - 1 : index - 1));
            } else if (event.key === 'Enter' && highlighted >= 0) {
                event.preventDefault();
                selectSuggestion(suggestions[highlighted]);
            }
        }
    };

    return (
        <div className="form-group nam-address-search">
            <label htmlFor="nam-address-query">Search address (NAM)</label>
            <div className="input-group">
                <input id="nam-address-query" ref={inputRef} className="form-control form-control-sm"
                    role="combobox" aria-autocomplete="list" aria-expanded={open}
                    aria-controls={open ? 'nam-address-suggestions' : undefined} aria-activedescendant={open && highlighted >= 0 ? `nam-address-option-${highlighted}` : undefined}
                    aria-describedby="nam-address-help nam-address-status"
                    autoComplete="off" disabled={!configured} value={query}
                    onChange={event => changeQuery(event.target.value)} onKeyDown={handleKeyDown}
                    onFocus={() => setOpen(suggestions.length > 0)}
                    onBlur={() => setOpen(false)}/>
                {interactionRequired && <div className="input-group-append">
                    <button type="button" className="btn btn-outline-info btn-sm" disabled={!configured || authorizing}
                        onClick={authorize}>Sign in to continue</button>
                </div>}
            </div>
            {open && <ul id="nam-address-suggestions" role="listbox" aria-label="NAM address suggestions" className="nam-suggestions">
                {suggestions.map((suggestion, index) => (
                    <li id={`nam-address-option-${index}`} role="option" aria-selected={highlighted === index}
                        key={`${suggestion.text}-${index}`} onMouseEnter={() => setHighlighted(index)}
                        onMouseDown={event => { event.preventDefault(); selectSuggestion(suggestion); }}>
                        {suggestion.text}
                    </li>
                ))}
            </ul>}
            <small id="nam-address-help" className="form-text text-muted">
                {configured ? 'Type at least 4 characters and select a complete address to fill the address fields below. Then select Get NamId.'
                    : 'NAM search is not configured. Set REACT_APP_NAM_BASEURL and REACT_APP_NAM_SCOPES; manual address search remains available.'}
            </small>
            <div id="nam-address-status" role={error ? 'alert' : 'status'} aria-live="polite">
                {error || (busy ? 'Looking up NAM address...' : '')}
            </div>
        </div>
    );
}
